import React, { useEffect, useState } from "react";
import { sendFeaturedPicksEmail } from "../api";

const PANEL = {
  padding: 18,
  border: "1px solid rgba(45,212,128,0.28)",
  borderRadius: 16,
  background: "linear-gradient(145deg, rgba(8,38,25,0.98), rgba(5,18,13,0.98))",
  color: "#eef8f1",
};

const CONTROL = {
  height: 40,
  padding: "0 11px",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 9,
  background: "#f8faf8",
  color: "#08130f",
};

const BUTTON = {
  minHeight: 40,
  padding: "0 15px",
  border: "1px solid rgba(45,212,128,0.65)",
  borderRadius: 9,
  background: "#2dd480",
  color: "#07170f",
  fontWeight: 850,
  cursor: "pointer",
};

const displayValue = (value) =>
  value === undefined || value === null || value === "" ? "—" : String(value);

export default function AdminFeaturedEmailPanel({ initialDay }) {
  const [premiumOnly, setPremiumOnly] = useState(false);
  const [day, setDay] = useState(initialDay || new Date().toISOString().slice(0, 10));
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (initialDay) setDay(initialDay);
  }, [initialDay]);

  const sendEmail = async () => {
    setSending(true);
    setResult(null);
    try {
      const data = await sendFeaturedPicksEmail({ day, premiumOnly });
      setResult({ ok: true, ...data });
    } catch (error) {
      setResult({
        ok: false,
        error: error?.response?.data?.detail || error?.message || "Could not send the daily picks email.",
      });
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  return (
    <section style={PANEL}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#2dd480", fontSize: 11, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase" }}>
            Audience delivery
          </div>
          <h3 style={{ margin: "5px 0 0", fontSize: 20 }}>Daily Featured Picks Email</h3>
          <p style={{ maxWidth: 780, margin: "7px 0 0", color: "#a9c0b2", fontSize: 13, lineHeight: 1.5 }}>
            Sends the selected day&apos;s featured card. Premium members receive every pick; free members receive the free card and a premium teaser.
          </p>
        </div>
        <span style={{ alignSelf: "flex-start", padding: "6px 10px", borderRadius: 999, background: "rgba(45,212,128,0.1)", color: "#86efac", fontSize: 12, fontWeight: 800 }}>
          Admin protected
        </span>
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "end", flexWrap: "wrap", marginTop: 16 }}>
        <label style={{ display: "grid", gap: 5, fontSize: 12, color: "#b9cec1" }}>
          Picks date
          <input type="date" value={day} onChange={(event) => setDay(event.target.value)} style={CONTROL} />
        </label>
        <label style={{ display: "inline-flex", gap: 8, alignItems: "center", minHeight: 40, fontSize: 13 }}>
          <input type="checkbox" checked={premiumOnly} onChange={(event) => setPremiumOnly(event.target.checked)} />
          Premium members only
        </label>
        <button type="button" style={{ ...BUTTON, opacity: sending ? 0.65 : 1 }} onClick={() => setConfirming(true)} disabled={sending || !day}>
          {sending ? "Sending…" : "Send Daily Email"}
        </button>
      </div>

      {result ? (
        <div style={{ marginTop: 16, padding: 14, borderRadius: 11, border: `1px solid ${result.ok ? "rgba(45,212,128,0.4)" : "rgba(248,113,113,0.45)"}`, background: result.ok ? "rgba(45,212,128,0.08)" : "rgba(248,113,113,0.08)" }}>
          {result.ok ? (
            <>
              <strong>Delivery complete</strong>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 10, marginTop: 12 }}>
                {[
                  ["Picks", result.picks_total],
                  ["Free", result.free_picks],
                  ["Premium", result.premium_picks],
                  ["Eligible", result.recipients],
                  ["Sent", result.sent],
                  ["Failed", result.failed],
                  ["Skipped", result.skipped_no_free],
                ].map(([label, value]) => (
                  <div key={label}>
                    <span style={{ display: "block", color: "#91aa9c", fontSize: 10, textTransform: "uppercase" }}>{label}</span>
                    <strong style={{ display: "block", marginTop: 3, fontSize: 18 }}>{displayValue(value)}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : <span style={{ color: "#fca5a5" }}>{result.error}</span>}
        </div>
      ) : null}

      {confirming ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "grid", placeItems: "center", padding: 18, background: "rgba(1,8,5,0.8)" }}>
          <div style={{ width: "min(500px, 100%)", padding: 20, borderRadius: 15, border: "1px solid rgba(45,212,128,0.38)", background: "#091a12", boxShadow: "0 24px 80px rgba(0,0,0,0.45)" }}>
            <div style={{ color: "#2dd480", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Confirm live send</div>
            <h3 style={{ margin: "7px 0 0" }}>Email the featured card now?</h3>
            <p style={{ color: "#adc3b5", fontSize: 13, lineHeight: 1.6 }}>
              Picks date: <strong>{day}</strong><br />
              Audience: <strong>{premiumOnly ? "Premium members only" : "All opted-in members"}</strong><br />
              This action sends real emails and cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 18 }}>
              <button type="button" style={{ ...BUTTON, background: "transparent", color: "#dce9df", borderColor: "rgba(255,255,255,0.2)" }} onClick={() => setConfirming(false)} disabled={sending}>Cancel</button>
              <button type="button" style={BUTTON} onClick={sendEmail} disabled={sending}>{sending ? "Sending…" : "Confirm and send"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
